import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coins, CalendarOff, Cat, Gem, Dice5, Ticket, Flame, Star, Crown, Clover, Gift } from "lucide-react";

const THEME = {
  jaguar: { bar: "bg-slate-900", text: "text-white", sub: "text-white/60", icon: Cat },
  magnum: { bar: "bg-yellow-400", text: "text-neutral-900", sub: "text-neutral-900/60", icon: Gem },
  damacai: { bar: "bg-blue-700", text: "text-white", sub: "text-white/60", icon: Dice5 },
  toto: { bar: "bg-red-600", text: "text-white", sub: "text-white/60", icon: Ticket },
  gdlotto: { bar: "bg-emerald-700", text: "text-white", sub: "text-white/60", icon: Flame },
  perdana: { bar: "bg-purple-700", text: "text-white", sub: "text-white/60", icon: Crown },
  ninelotto: { bar: "bg-cyan-700", text: "text-white", sub: "text-white/60", icon: Clover },
  lucky: { bar: "bg-pink-600", text: "text-white", sub: "text-white/60", icon: Gift },
  superj: { bar: "bg-orange-600", text: "text-white", sub: "text-white/60", icon: Star },
};

const sortStr = (s) => (s || "").split("").sort().join("");

const matchState = (value, highlight, permutation, favorites) => {
  const valid = value && value !== "----";
  const isHit = valid && highlight && (permutation ? sortStr(value) === sortStr(highlight) : value === highlight);
  const isFav = valid && !isHit && favorites && favorites.includes(value);
  return { isHit, isFav };
};

const NumberBox = ({ value, testId, highlight, permutation, favorites }) => {
  const prev = useRef(value);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prev.current !== value && value && value !== "----") {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1150);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);
  const blank = !value || value === "----";
  const { isHit, isFav } = matchState(value, highlight, permutation, favorites);
  const cls = isHit
    ? "border-emerald-500 bg-emerald-100 ring-2 ring-emerald-400"
    : isFav
    ? "border-amber-400 bg-amber-100 ring-2 ring-amber-300"
    : blank ? "border-slate-200 bg-slate-50/50" : "border-slate-200 bg-slate-50";
  const txt = isHit ? "text-emerald-700" : isFav ? "text-amber-700" : blank ? "text-slate-300" : "text-slate-800";
  return (
    <div data-testid={testId} className={`flex items-center justify-center rounded-lg border py-2 transition-colors ${cls} ${flash ? "flash-update" : ""}`}>
      <span className={`font-display tabular text-base font-bold tracking-wide sm:text-lg ${txt}`}>{value || "—"}</span>
    </div>
  );
};

const TopPrize = ({ rank, value, color, testId, highlight, permutation, favorites }) => {
  const { isHit, isFav } = matchState(value, highlight, permutation, favorites);
  const box = isHit ? "bg-emerald-100 ring-2 ring-emerald-400" : isFav ? "bg-amber-100 ring-2 ring-amber-300" : "bg-slate-50";
  const txt = isHit ? "text-emerald-700" : isFav ? "text-amber-700" : color;
  return (
    <div className={`flex flex-1 flex-col items-center rounded-xl py-3 text-center ${box}`}>
      <span className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-slate-400">{rank}</span>
      <span data-testid={testId} className={`font-display tabular text-3xl font-black leading-none tracking-tighter sm:text-[2.5rem] ${txt}`}>
        {value || "----"}
      </span>
    </div>
  );
};

const PODIUM = {
  1: { pedestal: "bg-amber-400", h: "h-20" },
  2: { pedestal: "bg-slate-300", h: "h-14" },
  3: { pedestal: "bg-orange-300", h: "h-10" },
};

const PodiumStep = ({ rank, value, order, shared, testId }) => {
  const p = PODIUM[rank];
  const { isHit, isFav } = matchState(value, shared.highlight, shared.permutation, shared.favorites);
  const numColor = isHit ? "text-emerald-600" : isFav ? "text-amber-600" : "text-slate-900";
  return (
    <div className={`flex flex-1 flex-col items-center ${order}`}>
      <span className={`mb-1.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-black text-white ${p.pedestal}`}>{rank}</span>
      <span data-testid={testId} className={`font-display tabular text-2xl font-black tracking-tighter sm:text-3xl ${numColor}`}>{value || "----"}</span>
    </div>
  );
};

const Podium = ({ result, shared }) => (
  <div className="flex flex-1 items-end justify-center gap-2 p-4 pt-6">
    <PodiumStep rank={2} value={result.second} order="order-1" shared={shared} testId={`podium-${result.provider}-2nd`} />
    <PodiumStep rank={1} value={result.first} order="order-2" shared={shared} testId={`podium-${result.provider}-1st`} />
    <PodiumStep rank={3} value={result.third} order="order-3" shared={shared} testId={`podium-${result.provider}-3rd`} />
  </div>
);

const numColorClassic = (value, shared, base) => {
  const { isHit, isFav } = matchState(value, shared.highlight, shared.permutation, shared.favorites);
  return isHit ? "text-emerald-600" : isFav ? "text-amber-600" : base;
};

const ClassicRow = ({ rank, value, shared, testId }) => (
  <div className="flex items-center justify-center gap-1.5 sm:gap-2">
    <span className="w-6 text-right text-[10px] font-semibold text-slate-400 sm:text-xs">{rank}</span>
    <span data-testid={testId} className={`font-display tabular text-base font-black tracking-wide sm:text-xl ${numColorClassic(value, shared, "text-blue-600")}`}>
      {value || "----"}
    </span>
  </div>
);

const ClassicGroup = ({ label, numbers, shared, provider, prefix }) => {
  const list = [...(numbers || [])].filter((v) => v && v !== "----").sort((a, b) => Number(a) - Number(b));
  if (!list.length) return null;
  return (
    <div>
      <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:text-xs">{label}</p>
      <div className="flex flex-wrap justify-center gap-x-1.5 gap-y-0.5 sm:gap-x-2.5 sm:gap-y-1">
        {list.map((n, i) => (
          <span key={`${prefix}-${i}-${n}`} data-testid={`${prefix}-${provider}-${i}`}
            className={`font-display tabular text-[11px] font-bold tracking-wide sm:text-sm ${numColorClassic(n, shared, "text-slate-800")}`}>
            {n}
          </span>
        ))}
      </div>
    </div>
  );
};

const ClassicCard = ({ result, shared, Icon }) => (
  <div className="flex flex-1 flex-col gap-2.5 p-2 sm:gap-3 sm:p-3">
    <div className="flex flex-col items-center gap-0.5">
      <Icon className="h-5 w-5 text-slate-700 sm:h-6 sm:w-6" />
      <h2 className="text-center font-display text-xs font-extrabold leading-tight text-slate-900 sm:text-base">{result.providerName}</h2>
      <span className="text-center text-[9px] font-medium text-slate-400 sm:text-[11px]">{result.date || "—"}</span>
    </div>
    <div className="mx-auto flex flex-col gap-0.5 sm:gap-1">
      <ClassicRow rank="1st" value={result.first} shared={shared} testId={`classic-${result.provider}-1st`} />
      <ClassicRow rank="2nd" value={result.second} shared={shared} testId={`classic-${result.provider}-2nd`} />
      <ClassicRow rank="3rd" value={result.third} shared={shared} testId={`classic-${result.provider}-3rd`} />
    </div>
    <ClassicGroup label="Special" numbers={result.special} shared={shared} provider={result.provider} prefix="csp" />
    <ClassicGroup label="Consolation" numbers={result.consolation} shared={shared} provider={result.provider} prefix="ccs" />
  </div>
);

export const ResultCard = ({ result, index, highlight, permutation, favorites, podium, classic }) => {
  const theme = THEME[result.provider] || THEME.jaguar;
  const Icon = theme.icon;
  const shared = { highlight, permutation, favorites };

  if (classic) {
    return (
      <motion.section
        data-testid={`result-card-${result.provider}-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: Math.min(index, 7) * 0.06, ease: "easeOut" }}
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
      >
        <div className={`h-1.5 ${theme.bar}`} />
        {!result.available ? (
          <div data-testid={`result-empty-${result.provider}`} className="flex flex-1 flex-col items-center justify-center gap-2 py-14 text-center">
            <CalendarOff className="h-6 w-6 text-slate-300" />
            <p className="text-sm font-medium text-slate-500">Tiada cabutan</p>
            <p className="text-xs text-slate-400">pada tarikh ini</p>
          </div>
        ) : (
          <ClassicCard result={result} shared={shared} Icon={Icon} />
        )}
      </motion.section>
    );
  }

  return (
    <motion.section
      data-testid={`result-card-${result.provider}-${index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index, 7) * 0.06, ease: "easeOut" }}
      className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
    >
      <div className={`flex items-center justify-between px-4 py-3.5 ${theme.bar}`}>
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-black/10">
            <Icon className={`h-5 w-5 ${theme.text}`} />
          </span>
          <div>
            <h2 className={`font-display text-lg font-extrabold leading-none ${theme.text}`}>{result.providerName}</h2>
            <span className={`text-[11px] font-medium ${theme.sub}`}>{result.title}</span>
          </div>
        </div>
        <div className={`text-right ${theme.sub}`}>
          <p className={`font-display text-xs font-bold ${theme.text}`}>{result.date || "—"}</p>
          {result.drawId ? <p className="text-[10px]">#{result.drawId}</p> : null}
        </div>
      </div>

      {!result.available ? (
        <div data-testid={`result-empty-${result.provider}`} className="flex flex-1 flex-col items-center justify-center gap-2 py-14 text-center">
          <CalendarOff className="h-6 w-6 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">Tiada cabutan</p>
          <p className="text-xs text-slate-400">pada tarikh ini</p>
        </div>
      ) : podium ? (
        <Podium result={result} shared={shared} />
      ) : (
        <div className="flex flex-1 flex-col gap-4 p-4">
          <div className="flex gap-2">
            <TopPrize rank="1st" value={result.first} color="text-red-600" {...shared} testId={`prize-${result.provider}-${index}-1st`} />
            <TopPrize rank="2nd" value={result.second} color="text-amber-500" {...shared} testId={`prize-${result.provider}-${index}-2nd`} />
            <TopPrize rank="3rd" value={result.third} color="text-emerald-600" {...shared} testId={`prize-${result.provider}-${index}-3rd`} />
          </div>

          {result.jackpot && result.jackpot !== "----" ? (
            <div className="flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-100 py-2 text-sm font-bold text-amber-800">
              <Coins className="h-4 w-4" /> Jackpot {result.jackpot}
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Special</p>
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {(result.special || []).map((n, i) => (
                <NumberBox key={`sp-${i}-${n}`} value={n} {...shared} testId={`sp-${result.provider}-${index}-${i}`} />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Consolation</p>
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {(result.consolation || []).map((n, i) => (
                <NumberBox key={`cs-${i}-${n}`} value={n} {...shared} testId={`cs-${result.provider}-${index}-${i}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.section>
  );
};
